import type { IConversationMessage } from "@next-platform/client-common-core";
import { NotificationType, useTranslation } from "@next-platform/client-common-core";
import useConversationRulebookPin from "@next-platform/client-common-core/rulebook/attach/useConversationRulebookPin";
import { type DropDownMenuItem, IconVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback, useMemo } from "react";

import useToast from "@/hooks/useToast";

type UseConversationRulebookMenuItemOptions = {
  conversationId?: string;
  /** Loaded messages: a bot message carrying Rulebook meta means the server already pinned one on that turn. */
  conversation: IConversationMessage[];
  /** Regenerated alternatives: a regenerate moves the evaluated reply here, but its pin still stands. */
  altMsgs?: IConversationMessage[];
  /** False for guests and group chats. */
  isAvailable: boolean;
};

/**
 * The chat menu's "Attach Rulebook" item and its picker's open state. Shown only while the chat can still take a pin:
 * the server pins once — explicitly or on the first turn a Rulebook resolves — and refuses any later change.
 */
export default function useConversationRulebookMenuItem({
  conversationId,
  conversation,
  altMsgs,
  isAvailable,
}: UseConversationRulebookMenuItemOptions) {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const notifyNeedsConversation = useCallback(
    () =>
      displayToast({
        message: t("chat:page.header.dropdown.attachRulebook.needsConversation", "Send a message first, then attach a Rulebook to this chat"),
        type: NotificationType.info,
      }),
    [displayToast, t],
  );
  const { isShown, isPickerOpen, openPicker, closePicker } = useConversationRulebookPin({
    conversationId,
    conversation,
    altMsgs,
    isAvailable,
    onNeedsConversation: notifyNeedsConversation,
  });

  const menuItem = useMemo<DropDownMenuItem>(
    () => ({
      icon: IconVariants.CHART_NO_AXES_COMBINED,
      label: t("chat:page.header.dropdown.attachRulebook.title", "Attach Rulebook"),
      onPress: openPicker,
      isHidden: !isShown,
      dataTestId: "chat-attach-rulebook-menu-item",
    }),
    [t, openPicker, isShown],
  );

  return { menuItem, isPickerOpen, closePicker };
}
